import { EmcatType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocDisplayType, LocMap, MapLocKeys } from '../../models';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Email Category Type|`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Core,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type SPEmcatTypeLocs = `SPEmcatTypeEnum${EmcatType}`;
export type CPEmcatTypeLocs = `CPEmcatTypeEnum${EmcatType}`;

export const spEmcatTypeEnumLocs: LocDefs<SPEmcatTypeLocs> = {
	SPEmcatTypeEnumAccountVerification: {
		defaultValue: 'Account Verification',
		displayPath: `${dpRoot}${EmcatType.AccountVerification}`,
		description: `System value for '${EmcatType.AccountVerification}' email category type`,
		...sharedSpLocData,
	},
	SPEmcatTypeEnumAuditComplete: {
		defaultValue: 'Audit Complete',
		displayPath: `${dpRoot}${EmcatType.AuditComplete}`,
		description: `System value for '${EmcatType.AuditComplete}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditComplete',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumAuditOnHold: {
		defaultValue: 'Audit On Hold',
		displayPath: `${dpRoot}${EmcatType.AuditOnHold}`,
		description: `System value for '${EmcatType.AuditOnHold}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditOnHold',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumAuditOnHoldReminder: {
		defaultValue: 'Audit On Hold Reminder',
		displayPath: `${dpRoot}${EmcatType.AuditOnHoldReminder}`,
		description: `System value for '${EmcatType.AuditOnHoldReminder}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditOnHoldReminder',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumBadAddress: {
		defaultValue: 'Bad Address',
		displayPath: `${dpRoot}${EmcatType.BadAddress}`,
		description: `System value for '${EmcatType.BadAddress}' email category type`,
		legacyName: 'CandidateProfile.Addresses/System.Enum.EmcatType.BadAddress',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumBenefitExpirationLong: {
		defaultValue: 'Benefit Expiration Long',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationLong}`,
		description: `System value for '${EmcatType.BenefitExpirationLong}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumBenefitExpirationMedium: {
		defaultValue: 'Benefit Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationMedium}`,
		description: `System value for '${EmcatType.BenefitExpirationMedium}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumBenefitExpirationShort: {
		defaultValue: 'Benefit Expiration Short',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationShort}`,
		description: `System value for '${EmcatType.BenefitExpirationShort}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumBenefitExpired: {
		defaultValue: 'Benefit Expired',
		displayPath: `${dpRoot}${EmcatType.BenefitExpired}`,
		description: `System value for '${EmcatType.BenefitExpired}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertExpired: {
		defaultValue: 'Certification Expired',
		displayPath: `${dpRoot}${EmcatType.CertExpired}`,
		description: `System value for '${EmcatType.CertExpired}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertExtension: {
		defaultValue: 'Certification Extension',
		displayPath: `${dpRoot}${EmcatType.CertExtension}`,
		description: `System value for '${EmcatType.CertExtension}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertExtension',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertRequirementCreated: {
		defaultValue: 'Certification Requirement Created',
		displayPath: `${dpRoot}${EmcatType.CertRequirementCreated}`,
		description: `System value for '${EmcatType.CertRequirementCreated}' email category type`,
		legacyName: 'OtherRequirement/System.Enum.EmcatType.CertRequirementCreated',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspended: {
		defaultValue: 'Certification Suspended',
		displayPath: `${dpRoot}${EmcatType.CertSuspended}`,
		description: `System value for '${EmcatType.CertSuspended}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspended',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspension2L: {
		defaultValue: 'Certification Suspension 2L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension2L}`,
		description: `System value for '${EmcatType.CertSuspension2L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension2L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspension3L: {
		defaultValue: 'Certification Suspension 3L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension3L}`,
		description: `System value for '${EmcatType.CertSuspension3L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension3L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspension4L: {
		defaultValue: 'Certification Suspension 4L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension4L}`,
		description: `System value for '${EmcatType.CertSuspension4L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension4L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspensionLong: {
		defaultValue: 'Certification Suspension Long',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionLong}`,
		description: `System value for '${EmcatType.CertSuspensionLong}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspensionMedium: {
		defaultValue: 'Certification Suspension Medium',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionMedium}`,
		description: `System value for '${EmcatType.CertSuspensionMedium}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertSuspensionShort: {
		defaultValue: 'Certification Suspension Short',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionShort}`,
		description: `System value for '${EmcatType.CertSuspensionShort}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCertification: {
		defaultValue: 'Certification',
		displayPath: `${dpRoot}${EmcatType.Certification}`,
		description: `System value for '${EmcatType.Certification}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Certification',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumEligibility: {
		defaultValue: 'Eligibility',
		displayPath: `${dpRoot}${EmcatType.Eligibility}`,
		description: `System value for '${EmcatType.Eligibility}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Eligibility',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamAccommodation: {
		defaultValue: 'Exam Accommodation',
		displayPath: `${dpRoot}${EmcatType.ExamAccommodation}`,
		description: `System value for '${EmcatType.ExamAccommodation}' email category type`,
		legacyName: 'Exam.Accommodations/System.Enum.EmcatType.ExamAccommodation',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamAuthExpirationLong: {
		defaultValue: 'Exam Authorization Expiration Long',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationLong}`,
		description: `System value for '${EmcatType.ExamAuthExpirationLong}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamAuthExpirationMedium: {
		defaultValue: 'Exam Authorization Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationMedium}`,
		description: `System value for '${EmcatType.ExamAuthExpirationMedium}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamAuthExpirationShort: {
		defaultValue: 'Exam Authorization Expiration Short',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationShort}`,
		description: `System value for '${EmcatType.ExamAuthExpirationShort}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamAuthExpired: {
		defaultValue: 'Exam Authorization Expired',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpired}`,
		description: `System value for '${EmcatType.ExamAuthExpired}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamEligibility: {
		defaultValue: 'Exam Eligibility',
		displayPath: `${dpRoot}${EmcatType.ExamEligibility}`,
		description: `System value for '${EmcatType.ExamEligibility}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamEligibility',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamExpirationLong: {
		defaultValue: 'Exam Expiration Long',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationLong}`,
		description: `System value for '${EmcatType.ExamExpirationLong}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamExpirationMedium: {
		defaultValue: 'Exam Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationMedium}`,
		description: `System value for '${EmcatType.ExamExpirationMedium}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamExpirationShort: {
		defaultValue: 'Exam Expiration Short',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationShort}`,
		description: `System value for '${EmcatType.ExamExpirationShort}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamScheduleReminder: {
		defaultValue: 'Exam Schedule Reminder',
		displayPath: `${dpRoot}${EmcatType.ExamScheduleReminder}`,
		description: `System value for '${EmcatType.ExamScheduleReminder}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.ExamScheduleReminder',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamScoreReportAvailable: {
		defaultValue: 'Exam Score Report Available',
		displayPath: `${dpRoot}${EmcatType.ExamScoreReportAvailable}`,
		description: `System value for '${EmcatType.ExamScoreReportAvailable}' email category type`,
		legacyName: 'Exam.ScoreReports/System.Enum.EmcatType.ExamScoreReportAvailable',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamViolationAdvancedSecurityRuleInvalidated: {
		defaultValue: 'Exam Violation Advanced Security Rule Invalidated',
		displayPath: `${dpRoot}${EmcatType.ExamViolationAdvancedSecurityRuleInvalidated}`,
		description: `System value for '${EmcatType.ExamViolationAdvancedSecurityRuleInvalidated}' email category type`,
		legacyName:
			'Exam.Security/System.Enum.EmcatType.ExamViolationAdvancedSecurityRuleInvalidated',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamViolationAdvancedSecurityRuleWarning: {
		defaultValue: 'Exam Violation Advanced Security Rule Warning',
		displayPath: `${dpRoot}${EmcatType.ExamViolationAdvancedSecurityRuleWarning}`,
		description: `System value for '${EmcatType.ExamViolationAdvancedSecurityRuleWarning}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationAdvancedSecurityRuleWarning',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamViolationInvalidated: {
		defaultValue: 'Exam Violation Invalidated',
		displayPath: `${dpRoot}${EmcatType.ExamViolationInvalidated}`,
		description: `System value for '${EmcatType.ExamViolationInvalidated}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationInvalidated',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExamViolationWarning: {
		defaultValue: 'Exam Violation Warning',
		displayPath: `${dpRoot}${EmcatType.ExamViolationWarning}`,
		description: `System value for '${EmcatType.ExamViolationWarning}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationWarning',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpire2L: {
		defaultValue: 'Expire 2L',
		displayPath: `${dpRoot}${EmcatType.Expire2L}`,
		description: `System value for '${EmcatType.Expire2L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire2L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpire3L: {
		defaultValue: 'Expire 3L',
		displayPath: `${dpRoot}${EmcatType.Expire3L}`,
		description: `System value for '${EmcatType.Expire3L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire3L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpire4L: {
		defaultValue: 'Expire 4L',
		displayPath: `${dpRoot}${EmcatType.Expire4L}`,
		description: `System value for '${EmcatType.Expire4L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire4L',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpireLong: {
		defaultValue: 'Expire Long',
		displayPath: `${dpRoot}${EmcatType.ExpireLong}`,
		description: `System value for '${EmcatType.ExpireLong}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpireMedium: {
		defaultValue: 'Expire Medium',
		displayPath: `${dpRoot}${EmcatType.ExpireMedium}`,
		description: `System value for '${EmcatType.ExpireMedium}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumExpireShort: {
		defaultValue: 'Expire Short',
		displayPath: `${dpRoot}${EmcatType.ExpireShort}`,
		description: `System value for '${EmcatType.ExpireShort}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumFailedExamReschedule: {
		defaultValue: 'Failed Exam Reschedule',
		displayPath: `${dpRoot}${EmcatType.FailedExamReschedule}`,
		description: `System value for '${EmcatType.FailedExamReschedule}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.FailedExamReschedule',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumFirstExam: {
		defaultValue: 'First Exam',
		displayPath: `${dpRoot}${EmcatType.FirstExam}`,
		description: `System value for '${EmcatType.FirstExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.FirstExam',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumFulfillment: {
		defaultValue: 'Fulfillment',
		displayPath: `${dpRoot}${EmcatType.Fulfillment}`,
		description: `System value for '${EmcatType.Fulfillment}' email category type`,
		legacyName: 'Credential.Fulfillment/System.Enum.EmcatType.Fulfillment',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumInvalid: {
		defaultValue: 'Invalid',
		displayPath: `${dpRoot}${EmcatType.Invalid}`,
		description: `System value for '${EmcatType.Invalid}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.Invalid',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumItemUnderReview: {
		defaultValue: 'Item Under Review',
		displayPath: `${dpRoot}${EmcatType.ItemUnderReview}`,
		description: `System value for '${EmcatType.ItemUnderReview}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.ItemUnderReview',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMerge: {
		defaultValue: 'Merge',
		displayPath: `${dpRoot}${EmcatType.Merge}`,
		description: `System value for '${EmcatType.Merge}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.Merge',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMergeConfirmation: {
		defaultValue: 'Merge Confirmation',
		displayPath: `${dpRoot}${EmcatType.MergeConfirmation}`,
		description: `System value for '${EmcatType.MergeConfirmation}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeConfirmation',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMergeRejected: {
		defaultValue: 'Merge Rejected',
		displayPath: `${dpRoot}${EmcatType.MergeRejected}`,
		description: `System value for '${EmcatType.MergeRejected}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeRejected',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMergeRequest: {
		defaultValue: 'Merge Request',
		displayPath: `${dpRoot}${EmcatType.MergeRequest}`,
		description: `System value for '${EmcatType.MergeRequest}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeRequest',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMiscellaneous: {
		defaultValue: 'Miscellaneous',
		displayPath: `${dpRoot}${EmcatType.Miscellaneous}`,
		description: `System value for '${EmcatType.Miscellaneous}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.Miscellaneous',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumNewBenefit: {
		defaultValue: 'New Benefit',
		displayPath: `${dpRoot}${EmcatType.NewBenefit}`,
		description: `System value for '${EmcatType.NewBenefit}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.NewBenefit',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumNewCandidate: {
		defaultValue: 'New Candidate',
		displayPath: `${dpRoot}${EmcatType.NewCandidate}`,
		description: `System value for '${EmcatType.NewCandidate}' email category type`,
		legacyName: 'CandidateAccess/System.Enum.EmcatType.NewCandidate',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumNewExam: {
		defaultValue: 'New Exam',
		displayPath: `${dpRoot}${EmcatType.NewExam}`,
		description: `System value for '${EmcatType.NewExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewExam',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumNewFailedExam: {
		defaultValue: 'New Failed Exam',
		displayPath: `${dpRoot}${EmcatType.NewFailedExam}`,
		description: `System value for '${EmcatType.NewFailedExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewFailedExam',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumNewPassedExam: {
		defaultValue: 'New Passed Exam',
		displayPath: `${dpRoot}${EmcatType.NewPassedExam}`,
		description: `System value for '${EmcatType.NewPassedExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewPassedExam',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumOtherRequirementStatusChange: {
		defaultValue: 'Other Requirement Status Change',
		displayPath: `${dpRoot}${EmcatType.OtherRequirementStatusChange}`,
		description: `System value for '${EmcatType.OtherRequirementStatusChange}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.OtherRequirementStatusChange',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumPaymentReminderLong: {
		defaultValue: 'Payment Reminder Long',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderLong}`,
		description: `System value for '${EmcatType.PaymentReminderLong}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumPaymentReminderMedium: {
		defaultValue: 'Payment Reminder Medium',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderMedium}`,
		description: `System value for '${EmcatType.PaymentReminderMedium}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumPaymentReminderShort: {
		defaultValue: 'Payment Reminder Short',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderShort}`,
		description: `System value for '${EmcatType.PaymentReminderShort}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireApproved: {
		defaultValue: 'Questionnaire Approved',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireApproved}`,
		description: `System value for '${EmcatType.QuestionnaireApproved}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireApproved',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireDeficient: {
		defaultValue: 'Questionnaire Deficient',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireDeficient}`,
		description: `System value for '${EmcatType.QuestionnaireDeficient}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireDeficient',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireDenied: {
		defaultValue: 'Questionnaire Denied',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireDenied}`,
		description: `System value for '${EmcatType.QuestionnaireDenied}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireDenied',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireEligible: {
		defaultValue: 'Questionnaire Eligible',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireEligible}`,
		description: `System value for '${EmcatType.QuestionnaireEligible}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireEligible',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireExpireLong: {
		defaultValue: 'Questionnaire Expire Long',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireLong}`,
		description: `System value for '${EmcatType.QuestionnaireExpireLong}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireExpireMedium: {
		defaultValue: 'Questionnaire Expire Medium',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireMedium}`,
		description: `System value for '${EmcatType.QuestionnaireExpireMedium}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireExpireShort: {
		defaultValue: 'Questionnaire Expire Short',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireShort}`,
		description: `System value for '${EmcatType.QuestionnaireExpireShort}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireExpired: {
		defaultValue: 'Questionnaire Expired',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpired}`,
		description: `System value for '${EmcatType.QuestionnaireExpired}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireResultExpireLong: {
		defaultValue: 'Questionnaire Result Expire Long',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireLong}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireLong}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireResultExpireMedium: {
		defaultValue: 'Questionnaire Result Expire Medium',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireMedium}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireMedium}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireResultExpireShort: {
		defaultValue: 'Questionnaire Result Expire Short',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireShort}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireShort}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumQuestionnaireResultExpired: {
		defaultValue: 'Questionnaire Result Expired',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpired}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpired}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumRestrictTesting: {
		defaultValue: 'Restrict Testing',
		displayPath: `${dpRoot}${EmcatType.RestrictTesting}`,
		description: `System value for '${EmcatType.RestrictTesting}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.RestrictTesting',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumRollingClockExpirationChange: {
		defaultValue: 'Rolling Clock Expiration Change',
		displayPath: `${dpRoot}${EmcatType.RollingClockExpirationChange}`,
		description: `System value for '${EmcatType.RollingClockExpirationChange}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.RollingClockExpirationChange',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumStandingCreated: {
		defaultValue: 'Standing Created',
		displayPath: `${dpRoot}${EmcatType.StandingCreated}`,
		description: `System value for '${EmcatType.StandingCreated}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingCreated',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumStandingExpireLong: {
		defaultValue: 'Standing Expire Long',
		displayPath: `${dpRoot}${EmcatType.StandingExpireLong}`,
		description: `System value for '${EmcatType.StandingExpireLong}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireLong',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumStandingExpireMedium: {
		defaultValue: 'Standing Expire Medium',
		displayPath: `${dpRoot}${EmcatType.StandingExpireMedium}`,
		description: `System value for '${EmcatType.StandingExpireMedium}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireMedium',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumStandingExpireShort: {
		defaultValue: 'Standing Expire Short',
		displayPath: `${dpRoot}${EmcatType.StandingExpireShort}`,
		description: `System value for '${EmcatType.StandingExpireShort}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireShort',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumStandingExpired: {
		defaultValue: 'Standing Expired',
		displayPath: `${dpRoot}${EmcatType.StandingExpired}`,
		description: `System value for '${EmcatType.StandingExpired}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpired',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebBadgeEmailSig: {
		defaultValue: 'Web Badge Email Sig',
		displayPath: `${dpRoot}${EmcatType.WebBadgeEmailSig}`,
		description: `System value for '${EmcatType.WebBadgeEmailSig}' email category type`,
		legacyName: 'Share.CertMetricsBadging/System.Enum.EmcatType.WebBadgeEmailSig',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebConversationAlert: {
		defaultValue: 'Web Conversation Alert',
		displayPath: `${dpRoot}${EmcatType.WebConversationAlert}`,
		description: `System value for '${EmcatType.WebConversationAlert}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebConversationAlert',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebEmailAddrReset: {
		defaultValue: 'Web Email Addr Reset',
		displayPath: `${dpRoot}${EmcatType.WebEmailAddrReset}`,
		description: `System value for '${EmcatType.WebEmailAddrReset}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebEmailAddrReset',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebFirstTimeLogin: {
		defaultValue: 'Web First Time Login',
		displayPath: `${dpRoot}${EmcatType.WebFirstTimeLogin}`,
		description: `System value for '${EmcatType.WebFirstTimeLogin}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebFirstTimeLogin',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebForgotPassword: {
		defaultValue: 'Web Forgot Password',
		displayPath: `${dpRoot}${EmcatType.WebForgotPassword}`,
		description: `System value for '${EmcatType.WebForgotPassword}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebForgotPassword',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebNameChange: {
		defaultValue: 'Web Name Change',
		displayPath: `${dpRoot}${EmcatType.WebNameChange}`,
		description: `System value for '${EmcatType.WebNameChange}' email category type`,
		legacyName: 'CandidateAccess/System.Enum.EmcatType.WebNameChange',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebSelfRegistration: {
		defaultValue: 'Web Self Registration',
		displayPath: `${dpRoot}${EmcatType.WebSelfRegistration}`,
		description: `System value for '${EmcatType.WebSelfRegistration}' email category type`,
		// Note: This entry also supports legacy names:
		// - CandidateAccess/System.Enum.EmcatType.WebSelfRegistration
		// - Core/System.Enum.EmcatType.WebSelfRegistration
		legacyName:
			'CandidateAccess.Login.SelfRegistration/System.Enum.EmcatType.WebSelfRegistration',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebTranscript: {
		defaultValue: 'Web Transcript',
		displayPath: `${dpRoot}${EmcatType.WebTranscript}`,
		description: `System value for '${EmcatType.WebTranscript}' email category type`,
		legacyName: 'Share.Transcript/System.Enum.EmcatType.WebTranscript',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebUserEmailAddressChanged: {
		defaultValue: 'Web User Email Address Changed',
		displayPath: `${dpRoot}${EmcatType.WebUserEmailAddressChanged}`,
		description: `System value for '${EmcatType.WebUserEmailAddressChanged}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserEmailAddressChanged',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebUserForgotPassword: {
		defaultValue: 'Web User Forgot Password',
		displayPath: `${dpRoot}${EmcatType.WebUserForgotPassword}`,
		description: `System value for '${EmcatType.WebUserForgotPassword}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserForgotPassword',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumWebUserTwoStepAuthentication: {
		defaultValue: 'Web User Two Step Authentication',
		displayPath: `${dpRoot}${EmcatType.WebUserTwoStepAuthentication}`,
		description: `System value for '${EmcatType.WebUserTwoStepAuthentication}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserTwoStepAuthentication',
		...sharedSpLocData,
	},
	SPEmcatTypeEnumMergeDenied: {
		defaultValue: 'Merge Denied',
		displayPath: `${dpRoot}${EmcatType.MergeDenied}`,
		description: `System value for '${EmcatType.MergeDenied}' email category type`,
		...sharedSpLocData,
	},
	SPEmcatTypeEnumCandidateBanned: {
		defaultValue: 'Candidate Banned',
		displayPath: `${dpRoot}CandidateBanned`,
		description: `System value for 'CandidateBanned' email category type`,
		...sharedSpLocData,
	},
};

export const cpEmcatTypeEnumLocs: LocDefs<CPEmcatTypeLocs> = {
	CPEmcatTypeEnumAccountVerification: {
		defaultValue: 'Account Verification',
		displayPath: `${dpRoot}${EmcatType.AccountVerification}`,
		description: `System value for '${EmcatType.AccountVerification}' email category type`,
		...sharedCpLocData,
	},
	CPEmcatTypeEnumAuditComplete: {
		defaultValue: 'Audit Complete',
		displayPath: `${dpRoot}${EmcatType.AuditComplete}`,
		description: `System value for '${EmcatType.AuditComplete}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditComplete',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumAuditOnHold: {
		defaultValue: 'Audit On Hold',
		displayPath: `${dpRoot}${EmcatType.AuditOnHold}`,
		description: `System value for '${EmcatType.AuditOnHold}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditOnHold',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumAuditOnHoldReminder: {
		defaultValue: 'Audit On Hold Reminder',
		displayPath: `${dpRoot}${EmcatType.AuditOnHoldReminder}`,
		description: `System value for '${EmcatType.AuditOnHoldReminder}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.AuditOnHoldReminder',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumBadAddress: {
		defaultValue: 'Bad Address',
		displayPath: `${dpRoot}${EmcatType.BadAddress}`,
		description: `System value for '${EmcatType.BadAddress}' email category type`,
		legacyName: 'CandidateProfile.Addresses/System.Enum.EmcatType.BadAddress',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumBenefitExpirationLong: {
		defaultValue: 'Benefit Expiration Long',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationLong}`,
		description: `System value for '${EmcatType.BenefitExpirationLong}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumBenefitExpirationMedium: {
		defaultValue: 'Benefit Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationMedium}`,
		description: `System value for '${EmcatType.BenefitExpirationMedium}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumBenefitExpirationShort: {
		defaultValue: 'Benefit Expiration Short',
		displayPath: `${dpRoot}${EmcatType.BenefitExpirationShort}`,
		description: `System value for '${EmcatType.BenefitExpirationShort}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpirationShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumBenefitExpired: {
		defaultValue: 'Benefit Expired',
		displayPath: `${dpRoot}${EmcatType.BenefitExpired}`,
		description: `System value for '${EmcatType.BenefitExpired}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.BenefitExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertExpired: {
		defaultValue: 'Certification Expired',
		displayPath: `${dpRoot}${EmcatType.CertExpired}`,
		description: `System value for '${EmcatType.CertExpired}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertExtension: {
		defaultValue: 'Certification Extension',
		displayPath: `${dpRoot}${EmcatType.CertExtension}`,
		description: `System value for '${EmcatType.CertExtension}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertExtension',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertRequirementCreated: {
		defaultValue: 'Certification Requirement Created',
		displayPath: `${dpRoot}${EmcatType.CertRequirementCreated}`,
		description: `System value for '${EmcatType.CertRequirementCreated}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertRequirementCreated',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspended: {
		defaultValue: 'Certification Suspended',
		displayPath: `${dpRoot}${EmcatType.CertSuspended}`,
		description: `System value for '${EmcatType.CertSuspended}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspended',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspension2L: {
		defaultValue: 'Certification Suspension 2L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension2L}`,
		description: `System value for '${EmcatType.CertSuspension2L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension2L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspension3L: {
		defaultValue: 'Certification Suspension 3L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension3L}`,
		description: `System value for '${EmcatType.CertSuspension3L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension3L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspension4L: {
		defaultValue: 'Certification Suspension 4L',
		displayPath: `${dpRoot}${EmcatType.CertSuspension4L}`,
		description: `System value for '${EmcatType.CertSuspension4L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspension4L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspensionLong: {
		defaultValue: 'Certification Suspension Long',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionLong}`,
		description: `System value for '${EmcatType.CertSuspensionLong}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspensionMedium: {
		defaultValue: 'Certification Suspension Medium',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionMedium}`,
		description: `System value for '${EmcatType.CertSuspensionMedium}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertSuspensionShort: {
		defaultValue: 'Certification Suspension Short',
		displayPath: `${dpRoot}${EmcatType.CertSuspensionShort}`,
		description: `System value for '${EmcatType.CertSuspensionShort}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.CertSuspensionShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCertification: {
		defaultValue: 'Certification',
		displayPath: `${dpRoot}${EmcatType.Certification}`,
		description: `System value for '${EmcatType.Certification}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Certification',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumEligibility: {
		defaultValue: 'Eligibility',
		displayPath: `${dpRoot}${EmcatType.Eligibility}`,
		description: `System value for '${EmcatType.Eligibility}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Eligibility',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamAccommodation: {
		defaultValue: 'Exam Accommodation',
		displayPath: `${dpRoot}${EmcatType.ExamAccommodation}`,
		description: `System value for '${EmcatType.ExamAccommodation}' email category type`,
		legacyName: 'Exam.Accommodations/System.Enum.EmcatType.ExamAccommodation',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamAuthExpirationLong: {
		defaultValue: 'Exam Authorization Expiration Long',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationLong}`,
		description: `System value for '${EmcatType.ExamAuthExpirationLong}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamAuthExpirationMedium: {
		defaultValue: 'Exam Authorization Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationMedium}`,
		description: `System value for '${EmcatType.ExamAuthExpirationMedium}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamAuthExpirationShort: {
		defaultValue: 'Exam Authorization Expiration Short',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpirationShort}`,
		description: `System value for '${EmcatType.ExamAuthExpirationShort}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpirationShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamAuthExpired: {
		defaultValue: 'Exam Authorization Expired',
		displayPath: `${dpRoot}${EmcatType.ExamAuthExpired}`,
		description: `System value for '${EmcatType.ExamAuthExpired}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamAuthExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamEligibility: {
		defaultValue: 'Exam Eligibility',
		displayPath: `${dpRoot}${EmcatType.ExamEligibility}`,
		description: `System value for '${EmcatType.ExamEligibility}' email category type`,
		legacyName: 'Exam.Authorization/System.Enum.EmcatType.ExamEligibility',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamExpirationLong: {
		defaultValue: 'Exam Expiration Long',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationLong}`,
		description: `System value for '${EmcatType.ExamExpirationLong}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamExpirationMedium: {
		defaultValue: 'Exam Expiration Medium',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationMedium}`,
		description: `System value for '${EmcatType.ExamExpirationMedium}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamExpirationShort: {
		defaultValue: 'Exam Expiration Short',
		displayPath: `${dpRoot}${EmcatType.ExamExpirationShort}`,
		description: `System value for '${EmcatType.ExamExpirationShort}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.ExamExpirationShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamScheduleReminder: {
		defaultValue: 'Exam Schedule Reminder',
		displayPath: `${dpRoot}${EmcatType.ExamScheduleReminder}`,
		description: `System value for '${EmcatType.ExamScheduleReminder}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.ExamScheduleReminder',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamScoreReportAvailable: {
		defaultValue: 'Exam Score Report Available',
		displayPath: `${dpRoot}${EmcatType.ExamScoreReportAvailable}`,
		description: `System value for '${EmcatType.ExamScoreReportAvailable}' email category type`,
		legacyName: 'Exam.ScoreReports/System.Enum.EmcatType.ExamScoreReportAvailable',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamViolationAdvancedSecurityRuleInvalidated: {
		defaultValue: 'Exam Violation Advanced Security Rule Invalidated',
		displayPath: `${dpRoot}${EmcatType.ExamViolationAdvancedSecurityRuleInvalidated}`,
		description: `System value for '${EmcatType.ExamViolationAdvancedSecurityRuleInvalidated}' email category type`,
		legacyName:
			'Exam.Security/System.Enum.EmcatType.ExamViolationAdvancedSecurityRuleInvalidated',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamViolationAdvancedSecurityRuleWarning: {
		defaultValue: 'Exam Violation Advanced Security Rule Warning',
		displayPath: `${dpRoot}${EmcatType.ExamViolationAdvancedSecurityRuleWarning}`,
		description: `System value for '${EmcatType.ExamViolationAdvancedSecurityRuleWarning}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationAdvancedSecurityRuleWarning',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamViolationInvalidated: {
		defaultValue: 'Exam Violation Invalidated',
		displayPath: `${dpRoot}${EmcatType.ExamViolationInvalidated}`,
		description: `System value for '${EmcatType.ExamViolationInvalidated}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationInvalidated',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExamViolationWarning: {
		defaultValue: 'Exam Violation Warning',
		displayPath: `${dpRoot}${EmcatType.ExamViolationWarning}`,
		description: `System value for '${EmcatType.ExamViolationWarning}' email category type`,
		legacyName: 'Exam.Security/System.Enum.EmcatType.ExamViolationWarning',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpire2L: {
		defaultValue: 'Expire 2L',
		displayPath: `${dpRoot}${EmcatType.Expire2L}`,
		description: `System value for '${EmcatType.Expire2L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire2L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpire3L: {
		defaultValue: 'Expire 3L',
		displayPath: `${dpRoot}${EmcatType.Expire3L}`,
		description: `System value for '${EmcatType.Expire3L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire3L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpire4L: {
		defaultValue: 'Expire 4L',
		displayPath: `${dpRoot}${EmcatType.Expire4L}`,
		description: `System value for '${EmcatType.Expire4L}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.Expire4L',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpireLong: {
		defaultValue: 'Expire Long',
		displayPath: `${dpRoot}${EmcatType.ExpireLong}`,
		description: `System value for '${EmcatType.ExpireLong}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpireMedium: {
		defaultValue: 'Expire Medium',
		displayPath: `${dpRoot}${EmcatType.ExpireMedium}`,
		description: `System value for '${EmcatType.ExpireMedium}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumExpireShort: {
		defaultValue: 'Expire Short',
		displayPath: `${dpRoot}${EmcatType.ExpireShort}`,
		description: `System value for '${EmcatType.ExpireShort}' email category type`,
		legacyName: 'Credential/System.Enum.EmcatType.ExpireShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumFailedExamReschedule: {
		defaultValue: 'Failed Exam Reschedule',
		displayPath: `${dpRoot}${EmcatType.FailedExamReschedule}`,
		description: `System value for '${EmcatType.FailedExamReschedule}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.FailedExamReschedule',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumFirstExam: {
		defaultValue: 'First Exam',
		displayPath: `${dpRoot}${EmcatType.FirstExam}`,
		description: `System value for '${EmcatType.FirstExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.FirstExam',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumFulfillment: {
		defaultValue: 'Fulfillment',
		displayPath: `${dpRoot}${EmcatType.Fulfillment}`,
		description: `System value for '${EmcatType.Fulfillment}' email category type`,
		legacyName: 'Credential.Fulfillment/System.Enum.EmcatType.Fulfillment',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumInvalid: {
		defaultValue: 'Invalid',
		displayPath: `${dpRoot}${EmcatType.Invalid}`,
		description: `System value for '${EmcatType.Invalid}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.Invalid',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumItemUnderReview: {
		defaultValue: 'Item Under Review',
		displayPath: `${dpRoot}${EmcatType.ItemUnderReview}`,
		description: `System value for '${EmcatType.ItemUnderReview}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.ItemUnderReview',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMerge: {
		defaultValue: 'Merge',
		displayPath: `${dpRoot}${EmcatType.Merge}`,
		description: `System value for '${EmcatType.Merge}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.Merge',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMergeConfirmation: {
		defaultValue: 'Merge Confirmation',
		displayPath: `${dpRoot}${EmcatType.MergeConfirmation}`,
		description: `System value for '${EmcatType.MergeConfirmation}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeConfirmation',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMergeRejected: {
		defaultValue: 'Merge Rejected',
		displayPath: `${dpRoot}${EmcatType.MergeRejected}`,
		description: `System value for '${EmcatType.MergeRejected}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeRejected',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMergeRequest: {
		defaultValue: 'Merge Request',
		displayPath: `${dpRoot}${EmcatType.MergeRequest}`,
		description: `System value for '${EmcatType.MergeRequest}' email category type`,
		legacyName: 'MergeCandidates/System.Enum.EmcatType.MergeRequest',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMiscellaneous: {
		defaultValue: 'Miscellaneous',
		displayPath: `${dpRoot}${EmcatType.Miscellaneous}`,
		description: `System value for '${EmcatType.Miscellaneous}' email category type`,
		legacyName: 'CandidateAccess/System.Enum.EmcatType.Miscellaneous',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumNewBenefit: {
		defaultValue: 'New Benefit',
		displayPath: `${dpRoot}${EmcatType.NewBenefit}`,
		description: `System value for '${EmcatType.NewBenefit}' email category type`,
		legacyName: 'Benefits/System.Enum.EmcatType.NewBenefit',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumNewCandidate: {
		defaultValue: 'New Candidate',
		displayPath: `${dpRoot}${EmcatType.NewCandidate}`,
		description: `System value for '${EmcatType.NewCandidate}' email category type`,
		legacyName: 'CandidateAccess/System.Enum.EmcatType.NewCandidate',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumNewExam: {
		defaultValue: 'New Exam',
		displayPath: `${dpRoot}${EmcatType.NewExam}`,
		description: `System value for '${EmcatType.NewExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewExam',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumNewFailedExam: {
		defaultValue: 'New Failed Exam',
		displayPath: `${dpRoot}${EmcatType.NewFailedExam}`,
		description: `System value for '${EmcatType.NewFailedExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewFailedExam',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumNewPassedExam: {
		defaultValue: 'New Passed Exam',
		displayPath: `${dpRoot}${EmcatType.NewPassedExam}`,
		description: `System value for '${EmcatType.NewPassedExam}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.NewPassedExam',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumOtherRequirementStatusChange: {
		defaultValue: 'Other Requirement Status Change',
		displayPath: `${dpRoot}${EmcatType.OtherRequirementStatusChange}`,
		description: `System value for '${EmcatType.OtherRequirementStatusChange}' email category type`,
		legacyName:
			'OtherRequirement.Upload.CandidateAccess.Audits/System.Enum.EmcatType.OtherRequirementStatusChange',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumPaymentReminderLong: {
		defaultValue: 'Payment Reminder Long',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderLong}`,
		description: `System value for '${EmcatType.PaymentReminderLong}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumPaymentReminderMedium: {
		defaultValue: 'Payment Reminder Medium',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderMedium}`,
		description: `System value for '${EmcatType.PaymentReminderMedium}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumPaymentReminderShort: {
		defaultValue: 'Payment Reminder Short',
		displayPath: `${dpRoot}${EmcatType.PaymentReminderShort}`,
		description: `System value for '${EmcatType.PaymentReminderShort}' email category type`,
		legacyName: 'Credential.ContinuingEducation/System.Enum.EmcatType.PaymentReminderShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireApproved: {
		defaultValue: 'Questionnaire Approved',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireApproved}`,
		description: `System value for '${EmcatType.QuestionnaireApproved}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireApproved',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireDeficient: {
		defaultValue: 'Questionnaire Deficient',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireDeficient}`,
		description: `System value for '${EmcatType.QuestionnaireDeficient}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireDeficient',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireDenied: {
		defaultValue: 'Questionnaire Denied',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireDenied}`,
		description: `System value for '${EmcatType.QuestionnaireDenied}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireDenied',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireEligible: {
		defaultValue: 'Questionnaire Eligible',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireEligible}`,
		description: `System value for '${EmcatType.QuestionnaireEligible}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireEligible',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireExpireLong: {
		defaultValue: 'Questionnaire Expire Long',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireLong}`,
		description: `System value for '${EmcatType.QuestionnaireExpireLong}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireExpireMedium: {
		defaultValue: 'Questionnaire Expire Medium',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireMedium}`,
		description: `System value for '${EmcatType.QuestionnaireExpireMedium}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireExpireShort: {
		defaultValue: 'Questionnaire Expire Short',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpireShort}`,
		description: `System value for '${EmcatType.QuestionnaireExpireShort}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpireShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireExpired: {
		defaultValue: 'Questionnaire Expired',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireExpired}`,
		description: `System value for '${EmcatType.QuestionnaireExpired}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireResultExpireLong: {
		defaultValue: 'Questionnaire Result Expire Long',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireLong}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireLong}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireResultExpireMedium: {
		defaultValue: 'Questionnaire Result Expire Medium',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireMedium}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireMedium}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireResultExpireShort: {
		defaultValue: 'Questionnaire Result Expire Short',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpireShort}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpireShort}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpireShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumQuestionnaireResultExpired: {
		defaultValue: 'Questionnaire Result Expired',
		displayPath: `${dpRoot}${EmcatType.QuestionnaireResultExpired}`,
		description: `System value for '${EmcatType.QuestionnaireResultExpired}' email category type`,
		legacyName: 'Questionnaires/System.Enum.EmcatType.QuestionnaireResultExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumRestrictTesting: {
		defaultValue: 'Restrict Testing',
		displayPath: `${dpRoot}${EmcatType.RestrictTesting}`,
		description: `System value for '${EmcatType.RestrictTesting}' email category type`,
		legacyName: 'Exam/System.Enum.EmcatType.RestrictTesting',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumRollingClockExpirationChange: {
		defaultValue: 'Rolling Clock Expiration Change',
		displayPath: `${dpRoot}${EmcatType.RollingClockExpirationChange}`,
		description: `System value for '${EmcatType.RollingClockExpirationChange}' email category type`,
		legacyName: 'Exam.Scheduling_Ncarb/System.Enum.EmcatType.RollingClockExpirationChange',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumStandingCreated: {
		defaultValue: 'Standing Created',
		displayPath: `${dpRoot}${EmcatType.StandingCreated}`,
		description: `System value for '${EmcatType.StandingCreated}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingCreated',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumStandingExpireLong: {
		defaultValue: 'Standing Expire Long',
		displayPath: `${dpRoot}${EmcatType.StandingExpireLong}`,
		description: `System value for '${EmcatType.StandingExpireLong}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireLong',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumStandingExpireMedium: {
		defaultValue: 'Standing Expire Medium',
		displayPath: `${dpRoot}${EmcatType.StandingExpireMedium}`,
		description: `System value for '${EmcatType.StandingExpireMedium}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireMedium',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumStandingExpireShort: {
		defaultValue: 'Standing Expire Short',
		displayPath: `${dpRoot}${EmcatType.StandingExpireShort}`,
		description: `System value for '${EmcatType.StandingExpireShort}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpireShort',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumStandingExpired: {
		defaultValue: 'Standing Expired',
		displayPath: `${dpRoot}${EmcatType.StandingExpired}`,
		description: `System value for '${EmcatType.StandingExpired}' email category type`,
		legacyName: 'Credential.Achievements/System.Enum.EmcatType.StandingExpired',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebBadgeEmailSig: {
		defaultValue: 'Web Badge Email Sig',
		displayPath: `${dpRoot}${EmcatType.WebBadgeEmailSig}`,
		description: `System value for '${EmcatType.WebBadgeEmailSig}' email category type`,
		legacyName: 'Share.CertMetricsBadging/System.Enum.EmcatType.WebBadgeEmailSig',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebConversationAlert: {
		defaultValue: 'Web Conversation Alert',
		displayPath: `${dpRoot}${EmcatType.WebConversationAlert}`,
		description: `System value for '${EmcatType.WebConversationAlert}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebConversationAlert',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebEmailAddrReset: {
		defaultValue: 'Web Email Addr Reset',
		displayPath: `${dpRoot}${EmcatType.WebEmailAddrReset}`,
		description: `System value for '${EmcatType.WebEmailAddrReset}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebEmailAddrReset',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebFirstTimeLogin: {
		defaultValue: 'Web First Time Login',
		displayPath: `${dpRoot}${EmcatType.WebFirstTimeLogin}`,
		description: `System value for '${EmcatType.WebFirstTimeLogin}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebFirstTimeLogin',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebForgotPassword: {
		defaultValue: 'Web Forgot Password',
		displayPath: `${dpRoot}${EmcatType.WebForgotPassword}`,
		description: `System value for '${EmcatType.WebForgotPassword}' email category type`,
		legacyName: 'CandidateAccess.Login/System.Enum.EmcatType.WebForgotPassword',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebNameChange: {
		defaultValue: 'Web Name Change',
		displayPath: `${dpRoot}${EmcatType.WebNameChange}`,
		description: `System value for '${EmcatType.WebNameChange}' email category type`,
		legacyName: 'CandidateAccess/System.Enum.EmcatType.WebNameChange',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebSelfRegistration: {
		defaultValue: 'Web Self Registration',
		displayPath: `${dpRoot}${EmcatType.WebSelfRegistration}`,
		description: `System value for '${EmcatType.WebSelfRegistration}' email category type`,
		// Note: This entry also supports legacy names:
		// - CandidateAccess/System.Enum.EmcatType.WebSelfRegistration
		// - Core/System.Enum.EmcatType.WebSelfRegistration
		legacyName:
			'CandidateAccess.Login.SelfRegistration/System.Enum.EmcatType.WebSelfRegistration',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebTranscript: {
		defaultValue: 'Web Transcript',
		displayPath: `${dpRoot}${EmcatType.WebTranscript}`,
		description: `System value for '${EmcatType.WebTranscript}' email category type`,
		legacyName: 'Share.Transcript/System.Enum.EmcatType.WebTranscript',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebUserEmailAddressChanged: {
		defaultValue: 'Web User Email Address Changed',
		displayPath: `${dpRoot}${EmcatType.WebUserEmailAddressChanged}`,
		description: `System value for '${EmcatType.WebUserEmailAddressChanged}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserEmailAddressChanged',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebUserForgotPassword: {
		defaultValue: 'Web User Forgot Password',
		displayPath: `${dpRoot}${EmcatType.WebUserForgotPassword}`,
		description: `System value for '${EmcatType.WebUserForgotPassword}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserForgotPassword',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumWebUserTwoStepAuthentication: {
		defaultValue: 'Web User Two Step Authentication',
		displayPath: `${dpRoot}${EmcatType.WebUserTwoStepAuthentication}`,
		description: `System value for '${EmcatType.WebUserTwoStepAuthentication}' email category type`,
		legacyName: 'Core/System.Enum.EmcatType.WebUserTwoStepAuthentication',
		...sharedCpLocData,
	},
	CPEmcatTypeEnumMergeDenied: {
		defaultValue: 'Merge Denied',
		displayPath: `${dpRoot}${EmcatType.MergeDenied}`,
		description: `System value for '${EmcatType.MergeDenied}' email category type`,
		...sharedCpLocData,
	},
	CPEmcatTypeEnumCandidateBanned: {
		defaultValue: 'Candidate Banned',
		displayPath: `${dpRoot}CandidateBanned`,
		description: `System value for 'CandidateBanned' email category type`,
		...sharedCpLocData,
	},
};

export const EX_AP_REVIEW_STATE_ENUM_LOCS = MapLocKeys<SPEmcatTypeLocs | CPEmcatTypeLocs>({
	...spEmcatTypeEnumLocs,
	...cpEmcatTypeEnumLocs,
});

export type SPEmcatTypeEnumLocType = LocMap<SPEmcatTypeLocs>;
export type CPEmcatTypeEnumLocType = LocMap<CPEmcatTypeLocs>;

export const spEmcatTypeKeys: {
	[key in EmcatType]: keyof SPEmcatTypeEnumLocType;
} = {
	[EmcatType.AccountVerification]: 'SPEmcatTypeEnumAccountVerification',
	[EmcatType.AuditComplete]: 'SPEmcatTypeEnumAuditComplete',
	[EmcatType.AuditOnHold]: 'SPEmcatTypeEnumAuditOnHold',
	[EmcatType.AuditOnHoldReminder]: 'SPEmcatTypeEnumAuditOnHoldReminder',
	[EmcatType.BadAddress]: 'SPEmcatTypeEnumBadAddress',
	[EmcatType.BenefitExpirationLong]: 'SPEmcatTypeEnumBenefitExpirationLong',
	[EmcatType.BenefitExpirationMedium]: 'SPEmcatTypeEnumBenefitExpirationMedium',
	[EmcatType.BenefitExpirationShort]: 'SPEmcatTypeEnumBenefitExpirationShort',
	[EmcatType.BenefitExpired]: 'SPEmcatTypeEnumBenefitExpired',
	[EmcatType.CertExpired]: 'SPEmcatTypeEnumCertExpired',
	[EmcatType.CertExtension]: 'SPEmcatTypeEnumCertExtension',
	[EmcatType.CertRequirementCreated]: 'SPEmcatTypeEnumCertRequirementCreated',
	[EmcatType.CertSuspended]: 'SPEmcatTypeEnumCertSuspended',
	[EmcatType.CertSuspension2L]: 'SPEmcatTypeEnumCertSuspension2L',
	[EmcatType.CertSuspension3L]: 'SPEmcatTypeEnumCertSuspension3L',
	[EmcatType.CertSuspension4L]: 'SPEmcatTypeEnumCertSuspension4L',
	[EmcatType.CertSuspensionLong]: 'SPEmcatTypeEnumCertSuspensionLong',
	[EmcatType.CertSuspensionMedium]: 'SPEmcatTypeEnumCertSuspensionMedium',
	[EmcatType.CertSuspensionShort]: 'SPEmcatTypeEnumCertSuspensionShort',
	[EmcatType.Certification]: 'SPEmcatTypeEnumCertification',
	[EmcatType.Eligibility]: 'SPEmcatTypeEnumEligibility',
	[EmcatType.ExamAccommodation]: 'SPEmcatTypeEnumExamAccommodation',
	[EmcatType.ExamAuthExpirationLong]: 'SPEmcatTypeEnumExamAuthExpirationLong',
	[EmcatType.ExamAuthExpirationMedium]: 'SPEmcatTypeEnumExamAuthExpirationMedium',
	[EmcatType.ExamAuthExpirationShort]: 'SPEmcatTypeEnumExamAuthExpirationShort',
	[EmcatType.ExamAuthExpired]: 'SPEmcatTypeEnumExamAuthExpired',
	[EmcatType.ExamEligibility]: 'SPEmcatTypeEnumExamEligibility',
	[EmcatType.ExamExpirationLong]: 'SPEmcatTypeEnumExamExpirationLong',
	[EmcatType.ExamExpirationMedium]: 'SPEmcatTypeEnumExamExpirationMedium',
	[EmcatType.ExamExpirationShort]: 'SPEmcatTypeEnumExamExpirationShort',
	[EmcatType.ExamScheduleReminder]: 'SPEmcatTypeEnumExamScheduleReminder',
	[EmcatType.ExamScoreReportAvailable]: 'SPEmcatTypeEnumExamScoreReportAvailable',
	[EmcatType.ExamViolationAdvancedSecurityRuleInvalidated]:
		'SPEmcatTypeEnumExamViolationAdvancedSecurityRuleInvalidated',
	[EmcatType.ExamViolationAdvancedSecurityRuleWarning]:
		'SPEmcatTypeEnumExamViolationAdvancedSecurityRuleWarning',
	[EmcatType.ExamViolationInvalidated]: 'SPEmcatTypeEnumExamViolationInvalidated',
	[EmcatType.ExamViolationWarning]: 'SPEmcatTypeEnumExamViolationWarning',
	[EmcatType.Expire2L]: 'SPEmcatTypeEnumExpire2L',
	[EmcatType.Expire3L]: 'SPEmcatTypeEnumExpire3L',
	[EmcatType.Expire4L]: 'SPEmcatTypeEnumExpire4L',
	[EmcatType.ExpireLong]: 'SPEmcatTypeEnumExpireLong',
	[EmcatType.ExpireMedium]: 'SPEmcatTypeEnumExpireMedium',
	[EmcatType.ExpireShort]: 'SPEmcatTypeEnumExpireShort',
	[EmcatType.FailedExamReschedule]: 'SPEmcatTypeEnumFailedExamReschedule',
	[EmcatType.FirstExam]: 'SPEmcatTypeEnumFirstExam',
	[EmcatType.Fulfillment]: 'SPEmcatTypeEnumFulfillment',
	[EmcatType.Invalid]: 'SPEmcatTypeEnumInvalid',
	[EmcatType.ItemUnderReview]: 'SPEmcatTypeEnumItemUnderReview',
	[EmcatType.Merge]: 'SPEmcatTypeEnumMerge',
	[EmcatType.MergeConfirmation]: 'SPEmcatTypeEnumMergeConfirmation',
	[EmcatType.MergeRejected]: 'SPEmcatTypeEnumMergeRejected',
	[EmcatType.MergeRequest]: 'SPEmcatTypeEnumMergeRequest',
	[EmcatType.Miscellaneous]: 'SPEmcatTypeEnumMiscellaneous',
	[EmcatType.NewBenefit]: 'SPEmcatTypeEnumNewBenefit',
	[EmcatType.NewCandidate]: 'SPEmcatTypeEnumNewCandidate',
	[EmcatType.NewExam]: 'SPEmcatTypeEnumNewExam',
	[EmcatType.NewFailedExam]: 'SPEmcatTypeEnumNewFailedExam',
	[EmcatType.NewPassedExam]: 'SPEmcatTypeEnumNewPassedExam',
	[EmcatType.OtherRequirementStatusChange]: 'SPEmcatTypeEnumOtherRequirementStatusChange',
	[EmcatType.PaymentReminderLong]: 'SPEmcatTypeEnumPaymentReminderLong',
	[EmcatType.PaymentReminderMedium]: 'SPEmcatTypeEnumPaymentReminderMedium',
	[EmcatType.PaymentReminderShort]: 'SPEmcatTypeEnumPaymentReminderShort',
	[EmcatType.QuestionnaireApproved]: 'SPEmcatTypeEnumQuestionnaireApproved',
	[EmcatType.QuestionnaireDeficient]: 'SPEmcatTypeEnumQuestionnaireDeficient',
	[EmcatType.QuestionnaireDenied]: 'SPEmcatTypeEnumQuestionnaireDenied',
	[EmcatType.QuestionnaireEligible]: 'SPEmcatTypeEnumQuestionnaireEligible',
	[EmcatType.QuestionnaireExpireLong]: 'SPEmcatTypeEnumQuestionnaireExpireLong',
	[EmcatType.QuestionnaireExpireMedium]: 'SPEmcatTypeEnumQuestionnaireExpireMedium',
	[EmcatType.QuestionnaireExpireShort]: 'SPEmcatTypeEnumQuestionnaireExpireShort',
	[EmcatType.QuestionnaireExpired]: 'SPEmcatTypeEnumQuestionnaireExpired',
	[EmcatType.QuestionnaireResultExpireLong]: 'SPEmcatTypeEnumQuestionnaireResultExpireLong',
	[EmcatType.QuestionnaireResultExpireMedium]: 'SPEmcatTypeEnumQuestionnaireResultExpireMedium',
	[EmcatType.QuestionnaireResultExpireShort]: 'SPEmcatTypeEnumQuestionnaireResultExpireShort',
	[EmcatType.QuestionnaireResultExpired]: 'SPEmcatTypeEnumQuestionnaireResultExpired',
	[EmcatType.RestrictTesting]: 'SPEmcatTypeEnumRestrictTesting',
	[EmcatType.RollingClockExpirationChange]: 'SPEmcatTypeEnumRollingClockExpirationChange',
	[EmcatType.StandingCreated]: 'SPEmcatTypeEnumStandingCreated',
	[EmcatType.StandingExpireLong]: 'SPEmcatTypeEnumStandingExpireLong',
	[EmcatType.StandingExpireMedium]: 'SPEmcatTypeEnumStandingExpireMedium',
	[EmcatType.StandingExpireShort]: 'SPEmcatTypeEnumStandingExpireShort',
	[EmcatType.StandingExpired]: 'SPEmcatTypeEnumStandingExpired',
	[EmcatType.WebBadgeEmailSig]: 'SPEmcatTypeEnumWebBadgeEmailSig',
	[EmcatType.WebConversationAlert]: 'SPEmcatTypeEnumWebConversationAlert',
	[EmcatType.WebEmailAddrReset]: 'SPEmcatTypeEnumWebEmailAddrReset',
	[EmcatType.WebFirstTimeLogin]: 'SPEmcatTypeEnumWebFirstTimeLogin',
	[EmcatType.WebForgotPassword]: 'SPEmcatTypeEnumWebForgotPassword',
	[EmcatType.WebNameChange]: 'SPEmcatTypeEnumWebNameChange',
	[EmcatType.WebSelfRegistration]: 'SPEmcatTypeEnumWebSelfRegistration',
	[EmcatType.WebTranscript]: 'SPEmcatTypeEnumWebTranscript',
	[EmcatType.WebUserEmailAddressChanged]: 'SPEmcatTypeEnumWebUserEmailAddressChanged',
	[EmcatType.WebUserForgotPassword]: 'SPEmcatTypeEnumWebUserForgotPassword',
	[EmcatType.WebUserTwoStepAuthentication]: 'SPEmcatTypeEnumWebUserTwoStepAuthentication',
	[EmcatType.MergeDenied]: 'SPEmcatTypeEnumMergeDenied',
	[EmcatType.CandidateBanned]: 'SPEmcatTypeEnumCandidateBanned',
};

export const cpEmcatTypeKeys: {
	[key in EmcatType]: keyof CPEmcatTypeEnumLocType;
} = {
	[EmcatType.AccountVerification]: 'CPEmcatTypeEnumAccountVerification',
	[EmcatType.AuditComplete]: 'CPEmcatTypeEnumAuditComplete',
	[EmcatType.AuditOnHold]: 'CPEmcatTypeEnumAuditOnHold',
	[EmcatType.AuditOnHoldReminder]: 'CPEmcatTypeEnumAuditOnHoldReminder',
	[EmcatType.BadAddress]: 'CPEmcatTypeEnumBadAddress',
	[EmcatType.BenefitExpirationLong]: 'CPEmcatTypeEnumBenefitExpirationLong',
	[EmcatType.BenefitExpirationMedium]: 'CPEmcatTypeEnumBenefitExpirationMedium',
	[EmcatType.BenefitExpirationShort]: 'CPEmcatTypeEnumBenefitExpirationShort',
	[EmcatType.BenefitExpired]: 'CPEmcatTypeEnumBenefitExpired',
	[EmcatType.CertExpired]: 'CPEmcatTypeEnumCertExpired',
	[EmcatType.CertExtension]: 'CPEmcatTypeEnumCertExtension',
	[EmcatType.CertRequirementCreated]: 'CPEmcatTypeEnumCertRequirementCreated',
	[EmcatType.CertSuspended]: 'CPEmcatTypeEnumCertSuspended',
	[EmcatType.CertSuspension2L]: 'CPEmcatTypeEnumCertSuspension2L',
	[EmcatType.CertSuspension3L]: 'CPEmcatTypeEnumCertSuspension3L',
	[EmcatType.CertSuspension4L]: 'CPEmcatTypeEnumCertSuspension4L',
	[EmcatType.CertSuspensionLong]: 'CPEmcatTypeEnumCertSuspensionLong',
	[EmcatType.CertSuspensionMedium]: 'CPEmcatTypeEnumCertSuspensionMedium',
	[EmcatType.CertSuspensionShort]: 'CPEmcatTypeEnumCertSuspensionShort',
	[EmcatType.Certification]: 'CPEmcatTypeEnumCertification',
	[EmcatType.Eligibility]: 'CPEmcatTypeEnumEligibility',
	[EmcatType.ExamAccommodation]: 'CPEmcatTypeEnumExamAccommodation',
	[EmcatType.ExamAuthExpirationLong]: 'CPEmcatTypeEnumExamAuthExpirationLong',
	[EmcatType.ExamAuthExpirationMedium]: 'CPEmcatTypeEnumExamAuthExpirationMedium',
	[EmcatType.ExamAuthExpirationShort]: 'CPEmcatTypeEnumExamAuthExpirationShort',
	[EmcatType.ExamAuthExpired]: 'CPEmcatTypeEnumExamAuthExpired',
	[EmcatType.ExamEligibility]: 'CPEmcatTypeEnumExamEligibility',
	[EmcatType.ExamExpirationLong]: 'CPEmcatTypeEnumExamExpirationLong',
	[EmcatType.ExamExpirationMedium]: 'CPEmcatTypeEnumExamExpirationMedium',
	[EmcatType.ExamExpirationShort]: 'CPEmcatTypeEnumExamExpirationShort',
	[EmcatType.ExamScheduleReminder]: 'CPEmcatTypeEnumExamScheduleReminder',
	[EmcatType.ExamScoreReportAvailable]: 'CPEmcatTypeEnumExamScoreReportAvailable',
	[EmcatType.ExamViolationAdvancedSecurityRuleInvalidated]:
		'CPEmcatTypeEnumExamViolationAdvancedSecurityRuleInvalidated',
	[EmcatType.ExamViolationAdvancedSecurityRuleWarning]:
		'CPEmcatTypeEnumExamViolationAdvancedSecurityRuleWarning',
	[EmcatType.ExamViolationInvalidated]: 'CPEmcatTypeEnumExamViolationInvalidated',
	[EmcatType.ExamViolationWarning]: 'CPEmcatTypeEnumExamViolationWarning',
	[EmcatType.Expire2L]: 'CPEmcatTypeEnumExpire2L',
	[EmcatType.Expire3L]: 'CPEmcatTypeEnumExpire3L',
	[EmcatType.Expire4L]: 'CPEmcatTypeEnumExpire4L',
	[EmcatType.ExpireLong]: 'CPEmcatTypeEnumExpireLong',
	[EmcatType.ExpireMedium]: 'CPEmcatTypeEnumExpireMedium',
	[EmcatType.ExpireShort]: 'CPEmcatTypeEnumExpireShort',
	[EmcatType.FailedExamReschedule]: 'CPEmcatTypeEnumFailedExamReschedule',
	[EmcatType.FirstExam]: 'CPEmcatTypeEnumFirstExam',
	[EmcatType.Fulfillment]: 'CPEmcatTypeEnumFulfillment',
	[EmcatType.Invalid]: 'CPEmcatTypeEnumInvalid',
	[EmcatType.ItemUnderReview]: 'CPEmcatTypeEnumItemUnderReview',
	[EmcatType.Merge]: 'CPEmcatTypeEnumMerge',
	[EmcatType.MergeConfirmation]: 'CPEmcatTypeEnumMergeConfirmation',
	[EmcatType.MergeRejected]: 'CPEmcatTypeEnumMergeRejected',
	[EmcatType.MergeRequest]: 'CPEmcatTypeEnumMergeRequest',
	[EmcatType.Miscellaneous]: 'CPEmcatTypeEnumMiscellaneous',
	[EmcatType.NewBenefit]: 'CPEmcatTypeEnumNewBenefit',
	[EmcatType.NewCandidate]: 'CPEmcatTypeEnumNewCandidate',
	[EmcatType.NewExam]: 'CPEmcatTypeEnumNewExam',
	[EmcatType.NewFailedExam]: 'CPEmcatTypeEnumNewFailedExam',
	[EmcatType.NewPassedExam]: 'CPEmcatTypeEnumNewPassedExam',
	[EmcatType.OtherRequirementStatusChange]: 'CPEmcatTypeEnumOtherRequirementStatusChange',
	[EmcatType.PaymentReminderLong]: 'CPEmcatTypeEnumPaymentReminderLong',
	[EmcatType.PaymentReminderMedium]: 'CPEmcatTypeEnumPaymentReminderMedium',
	[EmcatType.PaymentReminderShort]: 'CPEmcatTypeEnumPaymentReminderShort',
	[EmcatType.QuestionnaireApproved]: 'CPEmcatTypeEnumQuestionnaireApproved',
	[EmcatType.QuestionnaireDeficient]: 'CPEmcatTypeEnumQuestionnaireDeficient',
	[EmcatType.QuestionnaireDenied]: 'CPEmcatTypeEnumQuestionnaireDenied',
	[EmcatType.QuestionnaireEligible]: 'CPEmcatTypeEnumQuestionnaireEligible',
	[EmcatType.QuestionnaireExpireLong]: 'CPEmcatTypeEnumQuestionnaireExpireLong',
	[EmcatType.QuestionnaireExpireMedium]: 'CPEmcatTypeEnumQuestionnaireExpireMedium',
	[EmcatType.QuestionnaireExpireShort]: 'CPEmcatTypeEnumQuestionnaireExpireShort',
	[EmcatType.QuestionnaireExpired]: 'CPEmcatTypeEnumQuestionnaireExpired',
	[EmcatType.QuestionnaireResultExpireLong]: 'CPEmcatTypeEnumQuestionnaireResultExpireLong',
	[EmcatType.QuestionnaireResultExpireMedium]: 'CPEmcatTypeEnumQuestionnaireResultExpireMedium',
	[EmcatType.QuestionnaireResultExpireShort]: 'CPEmcatTypeEnumQuestionnaireResultExpireShort',
	[EmcatType.QuestionnaireResultExpired]: 'CPEmcatTypeEnumQuestionnaireResultExpired',
	[EmcatType.RestrictTesting]: 'CPEmcatTypeEnumRestrictTesting',
	[EmcatType.RollingClockExpirationChange]: 'CPEmcatTypeEnumRollingClockExpirationChange',
	[EmcatType.StandingCreated]: 'CPEmcatTypeEnumStandingCreated',
	[EmcatType.StandingExpireLong]: 'CPEmcatTypeEnumStandingExpireLong',
	[EmcatType.StandingExpireMedium]: 'CPEmcatTypeEnumStandingExpireMedium',
	[EmcatType.StandingExpireShort]: 'CPEmcatTypeEnumStandingExpireShort',
	[EmcatType.StandingExpired]: 'CPEmcatTypeEnumStandingExpired',
	[EmcatType.WebBadgeEmailSig]: 'CPEmcatTypeEnumWebBadgeEmailSig',
	[EmcatType.WebConversationAlert]: 'CPEmcatTypeEnumWebConversationAlert',
	[EmcatType.WebEmailAddrReset]: 'CPEmcatTypeEnumWebEmailAddrReset',
	[EmcatType.WebFirstTimeLogin]: 'CPEmcatTypeEnumWebFirstTimeLogin',
	[EmcatType.WebForgotPassword]: 'CPEmcatTypeEnumWebForgotPassword',
	[EmcatType.WebNameChange]: 'CPEmcatTypeEnumWebNameChange',
	[EmcatType.WebSelfRegistration]: 'CPEmcatTypeEnumWebSelfRegistration',
	[EmcatType.WebTranscript]: 'CPEmcatTypeEnumWebTranscript',
	[EmcatType.WebUserEmailAddressChanged]: 'CPEmcatTypeEnumWebUserEmailAddressChanged',
	[EmcatType.WebUserForgotPassword]: 'CPEmcatTypeEnumWebUserForgotPassword',
	[EmcatType.WebUserTwoStepAuthentication]: 'CPEmcatTypeEnumWebUserTwoStepAuthentication',
	[EmcatType.MergeDenied]: 'CPEmcatTypeEnumMergeDenied',
	[EmcatType.CandidateBanned]: 'CPEmcatTypeEnumCandidateBanned',
};
